import React, { useRef, forwardRef, useEffect } from "react";
import PropTypes from "prop-types";
import useStyles from "@hooks/useStyles";
import useStores from "@hooks/useStores";

import ContentPromo from "./ContentPromo";
import ContentTable from "./ContentTable";

import ContentSummary from "./ContentSummary";
import ContentAlert from "./ContentAlert";
import ContentVideo from "./ContentVideo";
import ContentImage from "./ContentImage";
import ContentAsset from "./ContentAsset";
import ContentMath from "./ContentMath";

import TableContainer from "@material-ui/core/TableContainer";
import Paper from "@material-ui/core/Paper";

import useStaticContext from "@hooks/useStaticContext";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import InnerHTML from "dangerously-set-html-content";

const ContentBody = ({}) => {
	const classes = useStyles("Content");

	const { contentStore: store } = useStores();

	store.refsReset();

	const renderHead = (H) => (_, children) => {
		const hyperLinkHead = (children) => {
			if (!children || typeof children[0] !== "string") return {};
			const child = children[0];
			const regex = new RegExp(/{#([a-zA-Z0-9].*)}/gm);
			const [_, id] = regex.exec(child) || [];
			children[0] = children[0].replace(regex, "").trim();
			return { response: children, id };
		};
		const { response, id } = hyperLinkHead(children);
		const ref = useRef();
		if (id) store.refs.push(ref);
		return (
			<H ref={ref} id={id}>
				{response}
			</H>
		);
	};

	const body = store.contentBody;
	const options = {
		renderNode: {
			[BLOCKS.HEADING_1]: renderHead(
				forwardRef(({ children, ...props }, ref) => (
					<h1 ref={ref} {...props}>
						{children}
					</h1>
				))
			),
			[BLOCKS.HEADING_2]: renderHead(
				forwardRef(({ children, ...props }, ref) => (
					<h2 ref={ref} {...props}>
						{children}
					</h2>
				))
			),
			[BLOCKS.HEADING_3]: renderHead(
				forwardRef(({ children, ...props }, ref) => (
					<h3 ref={ref} {...props}>
						{children}
					</h3>
				))
			),
			[BLOCKS.HEADING_4]: renderHead(
				forwardRef(({ children, ...props }, ref) => (
					<h4 ref={ref} {...props}>
						{children}
					</h4>
				))
			),
			[BLOCKS.HEADING_5]: renderHead(
				forwardRef(({ children, ...props }, ref) => (
					<h5 ref={ref} {...props}>
						{children}
					</h5>
				))
			),
			[BLOCKS.HEADING_6]: renderHead(
				forwardRef(({ children, ...props }, ref) => (
					<h6 ref={ref} {...props}>
						{children}
					</h6>
				))
			),
			[BLOCKS.EMBEDDED_ASSET]: (node) => {
				const asset = store.findLinkedEntity(node, body.links?.assets);
				if (!asset) return;
				return <ContentAsset {...asset} />;
			},
			[BLOCKS.EMBEDDED_ENTRY]: (node) => {
				const entry =
					node?.data?.__typename === "InjectedPromo"
						? node.data
						: store.findLinkedEntity(node, body.links?.entries);
				if (!entry) return;
				switch (entry.__typename) {
					case "Promo":
					case "InjectedPromo": // this comes from Content.store. Not from ContentFul.
						return <ContentPromo {...entry} />;
					case "Table":
						return <ContentTable {...entry} />;
					case "Image":
						return <ContentImage {...entry} />;
					case "Alert":
						return <ContentAlert {...entry} />;
					case "Summary":
						return <ContentSummary {...entry} />;
					case "Video":
						return <ContentVideo {...entry} />;
					case "Math":
						return <ContentMath {...entry} />;
					case "Calculator":
						return (
							<div className={classes.table}>
								<TableContainer component={Paper}>
									<InnerHTML html={entry.code} />
								</TableContainer>
							</div>
						);
				}
			},
			[INLINES.EMBEDDED_ENTRY]: (node) => {
				const entry = store.findLinkedEntity(node, body.links?.entries);
				if (!entry) return;
				switch (entry.__typename) {
					case "Math":
						return <ContentMath {...entry} />;
					case "Calculator":
						return (
							<div className={classes.table}>
								<TableContainer component={Paper}>
									<InnerHTML html={entry.code} />
								</TableContainer>
							</div>
						);
					case "Alert":
						return <ContentAlert {...entry} />;
					case "Summary":
						return <ContentSummary {...entry} />;
				}
			},
			[INLINES.ENTRY_HYPERLINK]: (node, children) => {
				const entry = store.findLinkedEntity(node, body.links?.entries);
				if (!entry) return;
				return (
					<a
						href={`${entry.folder}${entry.slug}`}
						target="_blank"
						rel="noreferrer"
					>
						{children}
					</a>
				);
			},
			[INLINES.HYPERLINK]: (node, children) => {
				return (
					<a href={`${node.data.uri}`} target="_blank" rel="noreferrer">
						{children}
					</a>
				);
			},
		},
	};
	return documentToReactComponents(store.content.body.json, options);
};

ContentBody.propTypes = {};
export default ContentBody;
